<script>
import store from '@state/store'
import { textOnColor } from '@modules/global/utils/Color'

// Loader de conteúdo com a logo do escritório (mesmo efeito de reveal do boot),
// para áreas médias/grandes. A logo é mostrada direto (sem caixa) sobre o fundo
// branco da plataforma. Decidimos o contorno pela cor do navbar (bg-navbar):
// navbar escuro => a logo é clara/branca => aplica contorno (drop-shadow) para
// ela não sumir no branco; navbar claro => a logo é escura => sem contorno.
// Cai no spinner quando não há logo (ou ela falha ao carregar). Para spinners
// pequenos (cards/linhas), use BaseLoading.
export default {
  name: 'BaseLogoLoading',
  props: {
    loading: Boolean,
    minHeight: {
      type: [Number, String],
      default: '50vh',
    },
    size: {
      type: [Number, String],
      default: 160,
    },
  },
  data() {
    return {
      imageFailed: false,
    }
  },
  computed: {
    mH() {
      return typeof this.minHeight === 'number'
        ? this.minHeight + 'px'
        : this.minHeight
    },
    // navbar escuro => textOnColor devolve '#FFFFFF' (texto claro) => a logo
    // exibida ali é clara => precisa de contorno no fundo branco da plataforma.
    logoNeedsOutline() {
      const bgNavbar = this.$Color()['bg-navbar'] || '#030309'
      return textOnColor(bgNavbar) === '#FFFFFF'
    },
    logoSource() {
      return store.getters['parameters/logo']
    },
    logoStyle() {
      const s = typeof this.size === 'number' ? this.size + 'px' : this.size
      return { width: s }
    },
    showLogo() {
      return !!this.logoSource && !this.imageFailed
    },
  },
  watch: {
    logoSource() {
      this.imageFailed = false
    },
  },
  methods: {
    onImageError() {
      this.imageFailed = true
    },
  },
}
</script>

<template>
  <div
    v-if="loading"
    class="logo-loading d-flex align-center justify-center"
    :style="{ minHeight: mH }"
  >
    <div v-if="showLogo" class="logo-loading__logo-wrap" :style="logoStyle">
      <img
        class="logo-loading__logo logo-loading__logo--ghost"
        :class="{ 'logo-loading__logo--outlined': logoNeedsOutline }"
        :src="logoSource"
        alt=""
        @error="onImageError"
      />
      <img
        class="logo-loading__logo logo-loading__logo--fill"
        :class="{ 'logo-loading__logo--outlined': logoNeedsOutline }"
        :src="logoSource"
        alt=""
        aria-hidden="true"
        @error="onImageError"
      />
    </div>

    <v-progress-circular
      v-else
      class="logo-loading__spinner"
      indeterminate
      color="primary"
      :size="40"
      :width="3"
    />
  </div>
</template>

<style lang="sass" scoped>
.logo-loading
  width: 100%

  &__logo-wrap
    position: relative
    display: inline-flex

  &__logo
    display: block
    width: 100%
    height: auto
    max-height: 22vh
    object-fit: contain

  // contorno sutil — só para logo clara/branca (navbar escuro), para ela ser
  // legível sobre o fundo branco da plataforma
  &__logo--outlined
    filter: drop-shadow(0 0 1.5px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 1.5px rgba(0, 0, 0, 0.35))

  // cópia base esmaecida, sempre visível
  &__logo--ghost
    opacity: 0.16

  // cópia em destaque, revelada da esquerda para a direita pelo clip animado
  &__logo--fill
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    opacity: 1
    animation: logoLoadingFill 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite

@keyframes logoLoadingFill
  0%
    clip-path: inset(0 100% 0 0)
    opacity: 1
  60%
    clip-path: inset(0 0 0 0)
    opacity: 1
  80%
    clip-path: inset(0 0 0 0)
    opacity: 1
  100%
    clip-path: inset(0 0 0 0)
    opacity: 0

@media (prefers-reduced-motion: reduce)
  .logo-loading__logo--fill
    animation: none
    clip-path: none
    opacity: 1
</style>
